import React, { memo, useCallback, useEffect, useMemo } from 'react'
import * as styles from './itemQuantityModal.module.scss'
import { Row, Col } from 'react-grid-system'
import { Item, ToolTypes } from '../../../../types'
import { AiOutlinePlusCircle, AiOutlineMinusCircle } from 'react-icons/ai'

import { Button, Modal } from '../../../common'

import { ItemQuantityModalProps } from './types'
import { Dropdown } from '../../../common/form/fields'
import { useWarehouses } from '../../../../hooks'
import { convertWarehouseOptionToOption } from '../../../common/crudItems/inventory/helpers'

const ItemQuantityModal = ({
   isModalOpen,
   handleClose,
   handleAdjustTotal,
   handleCustomTotalChange,
   startWorkflowAction,
   checkoutAction,
   items,
   selectedWarehouse,
   handleWarehouseChange,
   warehouseOptions,
   selectedTransferToWarehouse,
   handleTransferToWarehouseChange,
   enableLocationTracking,
   warehouseSelectionDisabled,
}: ItemQuantityModalProps) => {
   const toolId = useMemo(
      () => (items?.length > 0 ? items[0].Id : null),
      [items]
   )

   const {
      list: transferToWarehouseOptions,
      isLoading: isWarehousesLoading,
      isSuccess: isWarehousesSuccess,
   } = useWarehouses({
      toolId: toolId,
      currentLocationId: selectedWarehouse?.id,
   })

   useEffect(() => {
      if (isWarehousesSuccess && transferToWarehouseOptions?.length > 0) {
         const preSelectedOption = transferToWarehouseOptions.find(
            (opt) => opt?.Selected === true
         )
         const optionToSet = preSelectedOption
            ? convertWarehouseOptionToOption(preSelectedOption)
            : null
         handleTransferToWarehouseChange(optionToSet)
      }
   }, [transferToWarehouseOptions, isWarehousesSuccess])

   const actionType = useMemo(() => checkoutAction?.label, [checkoutAction])

   const isItemQuantityValid = useCallback(
      (item: Item) => {
         switch (actionType) {
            case 'Make Available':
            case 'Make Unavailable':
               return true
            case 'Field Transfer':
            case 'Return':
               return (
                  item.quantityForCart > 0 &&
                  item.quantityForCart <= item.StatusQuantity &&
                  !isNaN(item.quantityForCart)
               )
            case 'Warehouse Transfer':
            case 'Loan':
            case 'Borrow':
               return (
                  !selectedWarehouse ||
                  (item.quantityForCart > 0 &&
                     item.quantityForCart <= selectedWarehouse?.quantity &&
                     !isNaN(item.quantityForCart))
               )
         }
      },
      [actionType, selectedWarehouse]
   )

   const isQuantitiesValid = useMemo(
      () => items.every((item) => isItemQuantityValid(item)),
      [items]
   )

   const isQuantitiesDisabled = useMemo(() => {
      switch (actionType) {
         case 'Make Available':
         case 'Make Unavailable':
            return true
         case 'Field Transfer':
         case 'Return':
            return false
         case 'Warehouse Transfer':
            return !selectedWarehouse
         case 'Loan':
         case 'Borrow':
         default:
            return enableLocationTracking && !selectedWarehouse
      }
   }, [actionType, selectedWarehouse, enableLocationTracking])

   const isNextButtonDisabled = useMemo(
      () =>
         !isQuantitiesValid ||
         (actionType === 'Warehouse Transfer' &&
            (!selectedWarehouse || !selectedTransferToWarehouse)) ||
         (actionType === 'Loan' && !selectedWarehouse),
      [
         selectedTransferToWarehouse,
         selectedWarehouse,
         isQuantitiesValid,
         actionType,
      ]
   )

   const modalMessage = useMemo(() => {
      switch (actionType) {
         case 'Make Available':
         case 'Make Unavailable':
            return `Quantity Items can't have their availability changed. Total quantities shown will be made ${
               actionType === 'Make Available' ? 'available' : 'unavailable'
            }.`
         case 'Loan':
         case 'Borrow':
            return `Choose the quantity you want to ${actionType}${
               enableLocationTracking
                  ? ' from the desired Home Warehouse Location.'
                  : '.'
            }`
         case 'Warehouse Transfer':
            return 'Select the quantity, the source warehouse, and the destination warehouse for the transfer.'
         case 'Field Transfer':
         default:
            return `How many of the following items do you want to ${actionType}?`
      }
   }, [actionType, enableLocationTracking])

   const enableFromWarehouse = useMemo(() => {
      switch (actionType) {
         case 'Loan':
         case 'Borrow':
            return enableLocationTracking
         case 'Warehouse Transfer':
            return true
         default:
            return false
      }
   }, [actionType, enableLocationTracking])

   const enableToWarehouse = useMemo(
      () => actionType === 'Warehouse Transfer',
      [actionType]
   )

   const quantityAndConsumableItems = useMemo(
      () =>
         items.filter(
            (item) =>
               item.ToolType == ToolTypes['Consumable'] ||
               item.ToolType == ToolTypes['Quantity Item']
         ),
      [items]
   )

   const getInputClassName = useCallback(
      (isValid: boolean) => {
         if (isQuantitiesDisabled) {
            return styles.quantityInputDisabled
         } else if (isValid) {
            return styles.quantityInput
         } else {
            return styles.quantityInputInvalid
         }
      },
      [isQuantitiesDisabled]
   )

   const getCurrentQuantity = useCallback(
      (item: Item) => {
         if (actionType === 'Return' || actionType === 'Field Transfer') {
            return item?.quantityForCart
         } else if (!selectedWarehouse) {
            return ''
         } else if (isQuantitiesDisabled) {
            return selectedWarehouse?.quantity
         } else {
            return item?.quantityForCart
         }
      },
      [isQuantitiesDisabled, selectedWarehouse, actionType]
   )

   const warehouseFieldLabel = useMemo(
      () => (actionType?.includes('Transfer') ? 'Transfer' : actionType),
      [actionType]
   )

   const defaultWarehouse = useMemo(
      () =>
         selectedWarehouse
            ? warehouseOptions.find(
                 (wh) => Number(wh.value) === selectedWarehouse?.id
              )
            : null,
      [selectedWarehouse, warehouseOptions]
   )

   const defaultTransferToWarehouse = useMemo(() => {
      const rawOption = selectedTransferToWarehouse
         ? transferToWarehouseOptions.find(
              (wh) => Number(wh.Value) === selectedTransferToWarehouse?.id
           )
         : null
      return rawOption ? convertWarehouseOptionToOption(rawOption) : null
   }, [selectedTransferToWarehouse, transferToWarehouseOptions])

   const options = useMemo(
      () =>
         transferToWarehouseOptions?.length > 0
            ? transferToWarehouseOptions.map(convertWarehouseOptionToOption)
            : [],
      [transferToWarehouseOptions]
   )

   const getAvailableQuantity = useCallback(
      (item: Item) => {
         if (actionType === 'Return' || actionType === 'Field Transfer') {
            return item.StatusQuantity
         } else if (selectedWarehouse) {
            return selectedWarehouse?.quantity
         } else {
            return 0
         }
      },
      [selectedWarehouse, actionType]
   )

   const quantityStatus = useMemo(
      () => (actionType === 'Return' ? 'Loaned' : 'Available'),
      [actionType]
   )

   const getQuantitySource = useCallback(
      (item: Item) =>
         actionType === 'Return' ? item?.Title : defaultWarehouse?.label,
      [defaultWarehouse, actionType]
   )

   return (
      <Modal
         isModalVisible={isModalOpen}
         closeModal={handleClose}
         title={actionType}
         zIndex="999"
         stickyHeader
      >
         <div className={styles.warningModalMessageWrapper}>
            <p>{modalMessage}</p>
         </div>

         <section className={styles.contentWrapper}>
            {enableFromWarehouse && (
               <Row gutterWidth={20}>
                  <Col md={6}>
                     <h3>{warehouseFieldLabel} From:</h3>
                     <Dropdown
                        id="Warehouses"
                        name="Warehouses"
                        defaultValue={defaultWarehouse}
                        placeholder={
                           warehouseSelectionDisabled
                              ? 'Warehouse'
                              : 'Select Warehouse'
                        }
                        onChange={handleWarehouseChange}
                        options={warehouseOptions}
                        isMulti={false}
                        withWrappers={false}
                        isClearable
                        menuPosition="fixed"
                        disabled={warehouseSelectionDisabled}
                     />
                  </Col>
                  {enableToWarehouse && (
                     <Col md={6}>
                        <h3>{warehouseFieldLabel} To:</h3>
                        <Dropdown
                           id="WarehouseToTransferTo"
                           name="WarehouseToTransferTo"
                           defaultValue={defaultTransferToWarehouse}
                           placeholder="Select Warehouse"
                           onChange={handleTransferToWarehouseChange}
                           options={options}
                           isMulti={false}
                           withWrappers={false}
                           isClearable
                           menuPosition="fixed"
                           disabled={isWarehousesLoading}
                        />
                     </Col>
                  )}
               </Row>
            )}
            <Row>
               <Col md={12}>
                  <h3>Quantity:</h3>
                  {quantityAndConsumableItems.map((item, i) => {
                     const isValid = isItemQuantityValid(item)
                     return (
                        <div key={item.localUniqueId + '_' + i}>
                           <section className={styles.itemRow}>
                              <div
                                 className={
                                    !isQuantitiesDisabled
                                       ? styles.totalSelector
                                       : styles.totalSelectorDisabled
                                 }
                              >
                                 <button
                                    type="button"
                                    className={styles.adjustAmountButton}
                                    onClick={() => handleAdjustTotal(item, 'dec')}
                                    disabled={isQuantitiesDisabled}
                                    title="Decrease Quantity"
                                 >
                                    <AiOutlineMinusCircle size={25} />
                                 </button>
                                 <input
                                    className={getInputClassName(isValid)}
                                    type="number"
                                    id="quantity"
                                    name="quantity"
                                    min="1"
                                    max="10000"
                                    value={getCurrentQuantity(item)}
                                    onChange={(e) => handleCustomTotalChange(e, item)}
                                    disabled={isQuantitiesDisabled}
                                 ></input>

                                 <button
                                    type="button"
                                    className={styles.adjustAmountButton}
                                    onClick={() => handleAdjustTotal(item, 'inc')}
                                    disabled={isQuantitiesDisabled}
                                    title="Increase Quantity"
                                 >
                                    <AiOutlinePlusCircle size={25} />
                                 </button>
                              </div>
                              <span className={styles.availableTotal}>
                                 {getAvailableQuantity(item)} {quantityStatus}
                              </span>
                              <span className={styles.rowName}>
                                 {getQuantitySource(item)}
                              </span>
                           </section>
                           {!isValid && (
                              <p className={styles.invalidWarning}>
                                 Invalid quantity
                              </p>
                           )}
                        </div>
                     )
                  })}
            </Col>
         </Row>
         </section>
         <div className={styles.buttonRow}>
            <Row>
               <Col xs={12} sm={6}>
                  <div className={styles.buttonWrapper}>
                     <Button
                        type="button"
                        variant="primary"
                        onClick={startWorkflowAction}
                        isReset
                        disabled={isNextButtonDisabled}
                     >
                        Next
                     </Button>
                  </div>
               </Col>
               <Col xs={12} sm={6}>
                  <div className={styles.buttonWrapper}>
                     <Button
                        variant="tertiary"
                        onClick={handleClose}
                        type="button"
                        isReset
                     >
                        Cancel
                     </Button>
                  </div>
               </Col>
            </Row>
         </div>
      </Modal>
   )
}

export default memo(ItemQuantityModal)
